import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, ListGroup } from 'react-bootstrap';
import { CursosService } from '../../../../service/cursos.service';
import { Curso, Modulo, ProgresoResponse } from '../../../../service/types/cursos.types';
import CursoFirmaFinal from './CursoFirmaFinal';
import ModuloPlayer from './ModuloPlayer';

interface CursoPlayerProps {
  cursoId: string;
  onCursoCompletado?: () => void;
}

export default function CursoPlayer({ cursoId, onCursoCompletado }: CursoPlayerProps) {
  const [curso, setCurso] = useState<Curso | null>(null);
  const [progreso, setProgreso] = useState<ProgresoResponse | null>(null);
  const [moduloActivo, setModuloActivo] = useState<Modulo | null>(null);

  const load = async () => {
    const [cursoData, progresoData] = await Promise.all([
      CursosService.findOne(cursoId),
      CursosService.getProgreso(cursoId),
    ]);
    setCurso(cursoData);
    setProgreso(progresoData);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursoId]);

  const isModuloCompletado = (moduloId: string) =>
    !!progreso?.modulos.some((p) => p.modulo?.id === moduloId && p.completado);

  const handleModuloCompletado = async () => {
    setModuloActivo(null);
    await load();
  };

  const handleFirmado = async () => {
    await load();
    onCursoCompletado?.();
  };

  const handleDescargarCertificado = async () => {
    const blob = await CursosService.descargarCertificado(cursoId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificado-${curso?.titulo ?? cursoId}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!curso) return <p>Cargando curso...</p>;

  if (moduloActivo) {
    return <ModuloPlayer modulo={moduloActivo} onCompletado={handleModuloCompletado} onCerrar={() => setModuloActivo(null)} />;
  }

  const modulos = [...(curso.modulos ?? [])].sort((a, b) => a.orden - b.orden);
  const cursoCompletado = !!progreso?.curso?.completado;
  const todosModulosCompletos = modulos.length > 0 && modulos.every((m) => isModuloCompletado(m.id));
  const debeFirmar = todosModulosCompletos && !cursoCompletado;

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span>{curso.titulo}</span>
        <div className="d-flex align-items-center gap-2">
          {cursoCompletado && (
            <Button size="sm" variant="outline-success" onClick={handleDescargarCertificado}>
              Descargar certificado
            </Button>
          )}
          <Badge bg={cursoCompletado ? 'success' : 'secondary'}>{cursoCompletado ? 'Completado' : 'En progreso'}</Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {curso.descripcion && <p>{curso.descripcion}</p>}
        <p className="text-muted">Fecha límite: {curso.fechaLimite}</p>
        {curso.duracion != null && (
          <p className="text-muted">Duración aproximada: {curso.duracion} horas</p>
        )}
        {curso.vigencia != null && (
          <p className="text-muted">Vigencia: {curso.vigencia} meses desde que se completa</p>
        )}
        {!!progreso?.vecesCompletado && (
          <p className="text-muted">
            Ya completaste este curso {progreso.vecesCompletado}{' '}
            {progreso.vecesCompletado === 1 ? 'vez' : 'veces'} anteriormente.
          </p>
        )}
        {debeFirmar ? (
          <CursoFirmaFinal cursoId={cursoId} onFirmado={handleFirmado} />
        ) : (
          <ListGroup>
            {modulos.map((modulo) => {
              const completado = isModuloCompletado(modulo.id);
              return (
                <ListGroup.Item key={modulo.id} className="d-flex justify-content-between align-items-center">
                  <span>
                    {modulo.titulo}{' '}
                    {completado && <Badge bg="success">Completado</Badge>}
                  </span>
                  <Button size="sm" variant={completado ? 'outline-secondary' : 'primary'} onClick={() => setModuloActivo(modulo)}>
                    {completado ? 'Revisar' : 'Iniciar módulo'}
                  </Button>
                </ListGroup.Item>
              );
            })}
            {modulos.length === 0 && (
              <ListGroup.Item className="text-center text-muted">Este curso todavía no tiene módulos</ListGroup.Item>
            )}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  );
}
